import classNames from "classnames";
import { FunctionComponent, memo, useEffect, useMemo, useState } from "react";
import { IAnomaly, ModelType, Route } from "@edgetier/watchtower-types";
import { Box, ampersandList, FormattedDateTime, EllipsisMenu } from "@edgetier/client-components";
import { SpinnerUntil } from "@edgetier/components";
import { useParams } from "react-router-dom";
import { useQuery } from "react-query";

import AnomalyDetail from "components-for/anomalies/anomaly-detail";
import AnomalyModel from "components-for/anomalies/anomaly-model";
import AnomalyTagGroups from "components-for/anomalies/anomaly-tag-groups";
import InteractionsTable from "components-for/interactions/interactions-table";
import InteractionsTableDateTimeColumn from "components-for/interactions/interactions-table-date-time-column";
import InteractionsTableMessageColumn from "components-for/interactions/interactions-table-message-column";
import InteractionsTableMetricsColumn from "components-for/interactions/interactions-table-metrics-column";
import InteractionsTableSentimentColumn from "components-for/interactions/interactions-table-sentiment-column";
import ExploreLink from "components-for/navigation/explore-link";
import { Page, PageContent, PageHeader } from "@edgetier/client-components";
import useAnomalyEvents from "hooks-for/anomalies/use-anomaly-events";
import useModel from "hooks-for/models/use-model";
import useTags from "hooks-for/tags/use-tags";
import { axios } from "utilities/axios";
import { getAnomalyPhrases } from "utilities/get-anomaly-phrases";

import AnomalyEndDateTime from "./anomaly-end-date-time";
import AnomalyInteractionsCount from "./anomaly-interactions-count";
import AnomalyMetricTagGroups from "./anomaly-metric-tag-groups";
import AnomalyMetricValuesChart from "./anomaly-metric-values-chart";
import AnomalyPhraseChanges from "./anomaly-phrase-changes";
import AnomalyPhraseCountsChart from "./anomaly-phrase-counts-chart";
import AnomalySummaryValue from "./anomaly-summary-value";
import { ISelectedDateArea } from "pages/explore/explore-results/explore-results.types";
import { ANOMALY_PHRASES_ENABLED } from "./anomaly.constants";
import { IProps } from "./anomaly.types";
import AnomalyDisplayNameEdit from "components-for/anomalies/anomaly-display-name-edit";
import { trackUser } from "utilities/track-user";
import "./anomaly.scss";

/**
 * Page to show the details of an anomaly.
 * @returns Anomaly details.
 */
const Anomaly: FunctionComponent<IProps> = () => {
    const { anomalyId } = useParams<{ readonly anomalyId: string }>();
    const { data: tags, isLoading: tagsAreLoading } = useTags();

    // Request anomaly.
    const { data: anomaly, isLoading: anomalyIsLoading } = useQuery([Route.Anomaly, anomalyId], async () => {
        const response = await axios.get<IAnomaly>(Route.Anomaly.replace(":anomalyId", anomalyId));
        return response.data;
    });

    // Events belonging to the anomaly.
    const { data: anomalyEvents, isLoading: anomalyEventsAreLoading } = useAnomalyEvents(anomalyId);

    // Model that detected this anomaly.
    const { data: model, isLoading: modelIsLoading } = useModel(anomaly?.modelId!, {
        enabled: typeof anomaly !== "undefined",
    });

    const phrases = useMemo(() => getAnomalyPhrases(anomalyEvents || []), [anomalyEvents]);

    // An anomaly could be ongoing, so the end date time to use is just now. Need to memo this though so other
    // components aren't loaded automatically every time the component renders.
    const anomalyEndDateTime = useMemo(() => anomaly?.endDateTime ?? new Date(), [anomaly?.endDateTime]);

    const [dateFilterRange, setDateFilterRange] = useState<ISelectedDateArea>([
        anomaly?.startDateTime,
        anomaly?.endDateTime ?? new Date(),
    ]);
    useEffect(() => {
        if (typeof anomaly !== "undefined") {
            const startDate = anomaly.startDateTime;
            setDateFilterRange(([, end]) => [startDate, anomaly.endDateTime ?? end]);
        }
    }, [anomaly]);

    return (
        <Page className="anomaly">
            <SpinnerUntil
                data={[anomaly, anomalyEvents, !model]}
                isReady={!anomalyIsLoading && !anomalyEventsAreLoading && !modelIsLoading && !tagsAreLoading}
            >
                {!anomaly || !anomalyEvents || !model || !tags ? (
                    "Error"
                ) : (
                    <>
                        <PageHeader title={anomaly.displayName}>
                            <EllipsisMenu placement="bottom-center" menuLabel="Options" isVerticalEllipsis isButton>
                                {model.modelType === ModelType.Text && (
                                    <ExploreLink
                                        onClick={() => trackUser("Explore Anomaly", { anomalyId }, true)}
                                        parameters={{
                                            endDateTime: anomaly.endDateTime ?? undefined,
                                            languageIsoCode: model.configuration.languageIsoCode,
                                            search: phrases.join(" or "),
                                            startDateTime: anomaly.startDateTime,
                                            tagId: model.tags,
                                        }}
                                    />
                                )}

                                <AnomalyDisplayNameEdit
                                    anomalyId={anomaly.anomalyId}
                                    displayName={anomaly.displayName}
                                />
                            </EllipsisMenu>
                        </PageHeader>

                        <PageContent>
                            {model.model && <AnomalyModel model={model.model} />}
                            {Array.isArray(model.modelTags) && model.modelTags.length > 0 && (
                                <AnomalyTagGroups tags={model.modelTags} />
                            )}

                            {model.modelType === ModelType.Text && (
                                <AnomalyDetail
                                    label="Common Phrases"
                                    styleName="negative"
                                    value={ampersandList(phrases, { quote: true })}
                                />
                            )}

                            <div className="anomaly__summary">
                                <div className="anomaly__metrics">
                                    <AnomalyInteractionsCount
                                        anomalyId={anomalyId}
                                        endDateTime={anomalyEndDateTime}
                                        startDateTime={anomaly.startDateTime}
                                        title={
                                            model.modelType === ModelType.Metric
                                                ? "Interactions During Anomaly"
                                                : "Related Interactions"
                                        }
                                    />

                                    <AnomalySummaryValue title="Anomaly First Detected">
                                        <FormattedDateTime dateTime={anomaly.detectDateTime} />
                                    </AnomalySummaryValue>

                                    <AnomalyEndDateTime endDateTime={anomaly.endDateTime} />
                                </div>

                                <Box className="anomaly__charts">
                                    {model.modelType === ModelType.Metric && (
                                        <AnomalyMetricValuesChart
                                            startDateTime={anomaly.startDateTime}
                                            modelId={model.modelId}
                                            endDateTime={anomalyEndDateTime}
                                        />
                                    )}

                                    {model.modelType === ModelType.Text && (
                                        <AnomalyPhraseCountsChart
                                            anomalyId={anomalyId}
                                            anomalyEndDateTime={anomaly.endDateTime}
                                            anomalyStartDateTime={anomaly.startDateTime}
                                            dateTime={anomaly.startDateTime}
                                        />
                                    )}
                                </Box>
                            </div>

                            <div
                                className={classNames("anomaly__interactions", {
                                    "anomaly__interactions--metric-anomaly":
                                        ANOMALY_PHRASES_ENABLED && model.modelType === ModelType.Metric,
                                    "anomaly__interactions--text-anomaly":
                                        !ANOMALY_PHRASES_ENABLED || model.modelType === ModelType.Text,
                                })}
                            >
                                {Array.isArray(tags) && tags.length > 0 && (
                                    <AnomalyMetricTagGroups
                                        anomalyId={anomalyId}
                                        endDateTime={anomalyEndDateTime}
                                        startDateTime={anomaly.startDateTime}
                                    />
                                )}

                                {model.modelType === ModelType.Metric && ANOMALY_PHRASES_ENABLED && (
                                    <AnomalyPhraseChanges
                                        anomalyId={anomalyId}
                                        endDateTime={anomalyEndDateTime}
                                        startDateTime={anomaly.startDateTime}
                                    />
                                )}

                                <InteractionsTable
                                    defaultParameters={{
                                        endDateTime: dateFilterRange[1],
                                        startDateTime: dateFilterRange[0] ?? anomaly.startDateTime,
                                    }}
                                    mandatoryParameters={{
                                        anomalyId,
                                        interactionType:
                                            model.modelType === ModelType.Metric && model.interactionTypes.length > 0
                                                ? model.interactionTypes
                                                : undefined,
                                    }}
                                    title="Most Relevant Interactions"
                                >
                                    <InteractionsTableDateTimeColumn />
                                    <InteractionsTableMessageColumn />
                                    <InteractionsTableSentimentColumn />
                                    <InteractionsTableMetricsColumn />
                                </InteractionsTable>
                            </div>
                        </PageContent>
                    </>
                )}
            </SpinnerUntil>
        </Page>
    );
};

export default memo(Anomaly);
